import type { AnalystFunction } from '../schemas/analyst.js';
import { METHOD_NAME, type MethodContent } from '../schemas/method.js';
import type { NoyauCode } from '../schemas/noyau.js';

/**
 * THE SEED of Méthode (analyste.md §4.3, G25, issue #196): the version 1 the
 * singleton is born with, on the atelier, at its first start.
 *
 * It is the grid the code served the analyst until #196 (`GRID` of
 * `analyst-prompt.ts`, noyau.md §4.9), CUT INTO the fields of §4.2 - and nothing
 * the code did not say:
 *
 * - « Ce qu'elle désigne » is the line of each energy of the grid, whole;
 * - « Mots et expressions de la langue parlée » is the three FACES of the function
 *   at the three levels - what it is at the Pourquoi, at the Comment, at the Quoi,
 *   each with the words in brackets - in that order, WITHOUT the name of the level:
 *   the first pass is never told which level an objective carries (G10), so the
 *   words it reads must not name one;
 * - the NAME of a style comes from the meta-models of Patrick Mathieu (Révélation,
 *   Transfert, Transformation, Conquête, Désir / Perte, Configuration), the one
 *   thing the grid did not say;
 * - « Ce qui le reconnaît » is the formula of the noyau in the grid, as written;
 * - the typical words are empty: the list of concrete words Patrick sends is laid
 *   down afterwards, as a v2, from the tab or the assistant.
 *
 * Every fragment below is a substring of the system the old assembly produces
 * (`analystSystemPrompt`) - a test holds the seed to it, so « dérivée de la grille »
 * stays a checkable sentence and not a promise. The wording of a field is the
 * grid's, apostrophes included (the straight one).
 */

/** One function of the grid: the line it was given, and its three faces in the order of the levels. */
type FunctionSeed = {
  readonly designates: string;
  readonly faces: readonly [string, string, string];
};

const FUNCTION_SEED: Readonly<Record<AnalystFunction, FunctionSeed>> = {
  S: {
    designates:
      "La puissance et la référence. Les valeurs, le savoir, la transmission, le sens ; la maîtrise de l'espace et du temps, l'ordre, la juste place, la vue d'ensemble, ce qui dure.",
    faces: [
      'il règne (une vision, un ordre, des valeurs à transmettre)',
      'il maîtrise (comprendre, organiser, faire référence)',
      "il donne une place (un statut, une légitimité, l'accès à l'essentiel)",
    ],
  },
  G: {
    designates:
      "L'engagement et l'action. L'énergie, la performance, le défi, l'audace ; la rupture, la transformation, le dépassement, le progrès, le futur.",
    faces: [
      'une raison de se battre (un destin, un combat, un dépassement)',
      'une manière de conquérir (agir, oser, rompre, performer)',
      "une capacité d'agir (un progrès, un obstacle levé, une vie simplifiée)",
    ],
  },
  P: {
    designates:
      "La vie vécue. Les relations, les émotions, l'expérience, le beau ; les sensations, le désir, le plaisir, le soin, l'utilité, le présent et le quotidien.",
    faces: [
      'la vie comme cause (les gens, le vécu, le lien)',
      "la relation comme méthode (le soin, l'écoute, le collectif)",
      'le quotidien comme preuve (un bienfait concret, vécu, ici et maintenant)',
    ],
  },
};

/** One style: its name, and the formula of its noyau in the grid. */
type StyleSeed = { readonly name: string; readonly recognizes: string };

const STYLE_SEED: Readonly<Record<NoyauCode, StyleSeed>> = {
  SGP: {
    name: 'Révélation',
    recognizes:
      "Les maîtres de l'espace : que l'ordre règne et que chacun ait sa juste place, en élargissant activement leur univers pour y inclure tous ceux qu'ils peuvent, jusqu'à des bienfaits concrets.",
  },
  SPG: {
    name: 'Transfert',
    recognizes:
      "Les maîtres du temps : organiser la durée et protéger ce qui est essentiel à la vie, par le soin et la relation, jusqu'à un progrès qui permet d'agir.",
  },
  GSP: {
    name: 'Transformation',
    recognizes:
      "Prendre son destin en main et changer les règles, avec une vision et une maîtrise d'ensemble, jusqu'à un bien commun vécu au quotidien.",
  },
  GPS: {
    name: 'Conquête',
    recognizes:
      "Le dépassement et les défis matériels, relevés au contact du réel et avec les autres, jusqu'à une élévation et une reconnaissance.",
  },
  PGS: {
    name: 'Désir / Perte',
    recognizes:
      "Révéler la valeur de l'autre, avec intensité et réactivité, jusqu'à lui donner sa place.",
  },
  PSG: {
    name: 'Configuration',
    recognizes:
      "Enrichir la vie des gens, avec profondeur et savoir, jusqu'à ouvrir de nouvelles possibilités.",
  },
};

/** The three faces of a function, in one field: the three, joined by « ; », a full stop at the end. */
function wordsOf(seed: FunctionSeed): string {
  return `${seed.faces.join(' ; ')}.`;
}

/**
 * The CONTENT of the version 1 of Méthode: the grid of before #196, cut into the
 * fields of §4.2. The backend writes it once, as « graine #196 », at the first
 * start of an atelier (`seedMethodAtBoot`) - never on a vitrine, which receives
 * Méthode by « Passer en production » (K12).
 */
export const METHOD_SEED: MethodContent = {
  name: METHOD_NAME,
  functions: {
    S: { designates: FUNCTION_SEED.S.designates, words: wordsOf(FUNCTION_SEED.S) },
    G: { designates: FUNCTION_SEED.G.designates, words: wordsOf(FUNCTION_SEED.G) },
    P: { designates: FUNCTION_SEED.P.designates, words: wordsOf(FUNCTION_SEED.P) },
  },
  styles: {
    SGP: { ...STYLE_SEED.SGP, typicalWords: '' },
    SPG: { ...STYLE_SEED.SPG, typicalWords: '' },
    GSP: { ...STYLE_SEED.GSP, typicalWords: '' },
    GPS: { ...STYLE_SEED.GPS, typicalWords: '' },
    PGS: { ...STYLE_SEED.PGS, typicalWords: '' },
    PSG: { ...STYLE_SEED.PSG, typicalWords: '' },
  },
};
